/**
 * Mixin that detects when inline filters would overflow their container
 * and switches to the dropdown button.
 *
 * Measures the natural width of inline filter content once,
 * then on resize compares available space against that threshold.
 * No layout thrashing, no flickering.
 */
export default {
    data() {
        return {
            filterOverflowObserver: null,
            filterContentWidth: 0,
        };
    },
    methods: {
        measureFilterContentWidth() {
            const inline = this.$el ? this.$el.querySelector('.filters-inline') : null;
            if (!inline) return 0;

            // Sum up the width of all direct children + gaps
            const children = inline.children;
            let totalWidth = 0;
            for (let i = 0; i < children.length; i++) {
                totalWidth += children[i].getBoundingClientRect().width;
            }
            // Add gaps (0.4rem × (n-1) children, roughly 6px per gap) + safety margin
            const gapCount = Math.max(0, children.length - 1);
            totalWidth += gapCount * 6;

            return Math.ceil(totalWidth) + 40;
        },
        checkFilterOverflow() {
            const inline = this.$el ? this.$el.querySelector('.filters-inline') : null;
            const dropdown = this.$el ? this.$el.querySelector('.filter-dropdown') : null;
            if (!inline || !dropdown) return;

            const headerRow = inline.closest('.header-row');
            if (!headerRow) return;

            // Get the title width to calculate available space
            // In edit mode the title is an input for the custom name
            const title = headerRow.querySelector('.title') || headerRow.querySelector('.widget-title-input');
            const refreshIcon = headerRow.querySelector('.force-update-icon') || headerRow.querySelector('.spinner');
            const titleWidth = title ? title.getBoundingClientRect().width : 0;
            const iconWidth = refreshIcon ? refreshIcon.getBoundingClientRect().width : 0;
            const headerGaps = 20; // gaps between title, icon, filters
            const headerWidth = headerRow.getBoundingClientRect().width;
            const availableWidth = headerWidth - titleWidth - iconWidth - headerGaps;

            // Measure content width if not measured yet or if it could have changed
            if (this.filterContentWidth === 0) {
                // Temporarily show inline to measure
                inline.classList.remove('is-overflowing');
                dropdown.classList.remove('is-visible');
                this.filterContentWidth = this.measureFilterContentWidth();
            }

            const isOverflowing = this.filterContentWidth > availableWidth;

            if (isOverflowing) {
                inline.classList.add('is-overflowing');
                dropdown.classList.add('is-visible');
            } else {
                inline.classList.remove('is-overflowing');
                dropdown.classList.remove('is-visible');
            }
        },
        initFilterOverflowObserver() {
            const headerRow = this.$el ? this.$el.querySelector('.header-row') : null;
            if (!headerRow) return;

            this.filterOverflowObserver = new ResizeObserver(() => {
                this.checkFilterOverflow();
            });
            this.filterOverflowObserver.observe(headerRow);

            // Initial check after layout settles
            this.$nextTick(() => {
                this.checkFilterOverflow();
            });
        },
        destroyFilterOverflowObserver() {
            if (this.filterOverflowObserver) {
                this.filterOverflowObserver.disconnect();
                this.filterOverflowObserver = null;
            }
        },
        // Call this when filter content changes (e.g., multiselect tags added/removed)
        resetFilterOverflowMeasurement() {
            this.filterContentWidth = 0;
            this.checkFilterOverflow();
        },
    },
    mounted() {
        this.initFilterOverflowObserver();
    },
    beforeDestroy() {
        this.destroyFilterOverflowObserver();
    },
};
